import { useTranslation } from 'react-i18next';
import type { Assistant } from '@/application/assistants/assistantsTypes';
import type { Role } from '@/application/permissions/permissionsTypes';
import {
  AssistantActions,
  AssistantStatusBadge,
  assistantInitials,
  assistantRoleName,
} from '@/presentation/components/assistants/AssistantActions';

export function AssistantCard({
  assistant,
  roles,
  onEdit,
  onRecoverPassword,
  onResetTwoFactor,
  onToggleStatus,
  onDelete,
}: {
  assistant: Assistant;
  roles: Role[];
  onEdit: (assistant: Assistant) => void;
  onRecoverPassword: (assistant: Assistant) => void;
  onResetTwoFactor: (assistant: Assistant) => void;
  onToggleStatus: (assistant: Assistant) => Promise<void>;
  onDelete: (assistant: Assistant) => void;
}) {
  const { t } = useTranslation();
  const roleName = assistantRoleName(assistant, roles, t('assistants.role.none'));

  return (
    <article className="space-y-3">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-600 text-sm font-bold text-white"
        >
          {assistantInitials(assistant.nombre)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate font-semibold text-slate-900 dark:text-white">{assistant.nombre}</p>
            <AssistantStatusBadge status={assistant.status} />
          </div>
          <p className="truncate text-sm text-slate-500 dark:text-slate-400">{assistant.email}</p>
          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
            {roleName}
            <span aria-hidden className="px-1.5">
              ·
            </span>
            {t('assistants.clinics.count', { count: assistant.clinicaIds.length })}
          </p>
        </div>
      </div>
      <AssistantActions
        assistant={assistant}
        layout="mobile"
        onEdit={onEdit}
        onRecoverPassword={onRecoverPassword}
        onResetTwoFactor={onResetTwoFactor}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
      />
    </article>
  );
}
